import React, { useEffect, useState } from "react";
import axios from "axios";
import Layout from "../../components/layout";
import { Table, Card, Row, Col, Progress, Tag, Avatar, Typography, Flex} from "antd";
import {
  PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell
} from "recharts";
import {
  ArrowUpOutlined, ArrowDownOutlined, TeamOutlined,
  CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined,
  MedicineBoxOutlined, UserAddOutlined, SyncOutlined,
  DashboardOutlined,StopOutlined, ExclamationOutlined,FileExcelOutlined,CloseSquareOutlined
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "../../redux/alertsSlice";

const { Title, Text } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const TITLE_SLATE      = "#ecedf0ff";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Chart palette ──────────────────────────────────────────── */
const CHART_COLORS = [EMERALD, "#6366f1", "#f59e0b", "#ef4444", "#8b5cf6"];

/* ─── Style injection ────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("admin-home-styles")) return;
  const el = document.createElement("style");
  el.id = "admin-home-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .adm-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }
    .adm-wrap { max-width: 1400px; margin: 0 auto; padding: 32px 24px 64px; }

    /* Page header */
    .adm-hero {
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      border-radius: 24px; padding: 36px 44px;
      position: relative; overflow: hidden; margin-bottom: 36px;
    }
    .adm-hero::before {
      content: ''; position: absolute; top: -80px; right: -80px;
      width: 320px; height: 320px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, transparent 70%);
      filter: blur(32px); pointer-events: none;
    }

    /* Stat card */
    .adm-stat {
      border-radius: 20px !important; border: none !important;
      box-shadow: 0 4px 20px rgba(0,0,0,.06) !important;
      transition: transform .22s, box-shadow .22s !important;
      overflow: hidden;
    }
    .adm-stat:hover { transform: translateY(-4px) !important; box-shadow: 0 16px 36px rgba(0,0,0,.1) !important; }

    /* Chart card */
    .adm-chart-card {
      border-radius: 20px !important; border: none !important;
      box-shadow: 0 4px 20px rgba(0,0,0,.06) !important;
    }
    .adm-chart-card .ant-card-head {
      border-bottom: 1px solid #f1f5f9 !important;
      padding: 16px 24px !important; min-height: unset !important;
    }
    .adm-chart-card .ant-card-head-title { font-family: 'Sora', sans-serif !important; font-size: 15px !important; color: ${TITLE_SLATE} !important; font-weight: 700 !important; }

    /* Table card */
    .adm-table-card {
      border-radius: 20px !important; border: none !important;
      box-shadow: 0 4px 20px rgba(0,0,0,.06) !important;
    }
    .adm-table-card .ant-card-head { border-bottom: 1px solid #f1f5f9 !important; padding: 16px 24px !important; min-height: unset !important; }
    .adm-table-card .ant-card-head-title { font-family: 'Sora', sans-serif !important; font-size: 15px !important; color: ${TITLE_SLATE} !important; font-weight: 700 !important; }
    .adm-table-card .ant-table-thead > tr > th { background: ${SURFACE} !important; color: ${MUTED} !important; font-size: 11px !important; font-weight: 700 !important; letter-spacing: 1px !important; text-transform: uppercase !important; border-bottom: 1px solid #f1f5f9 !important; }
    .adm-table-card .ant-table-tbody > tr > td { border-bottom: 1px solid #f8fafc !important; }
    .adm-table-card .ant-table-tbody > tr:hover > td { background: #f0fdfb !important; }

    /* Section eyebrow */
    .adm-eyebrow {
      display: inline-block; padding: 4px 12px; border-radius: 20px;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
      font-family: 'Sora', sans-serif; margin-bottom: 12px;
    }

    /* Empty chart state */
    .adm-chart-empty { text-align: center; padding: 50px 20px; color: ${MUTED}; font-size: 14px; }

    /* Custom tooltip */
    .adm-tooltip { background: ${WHITE}; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; font-family: 'DM Sans', sans-serif; font-size: 13px; box-shadow: 0 8px 24px rgba(0,0,0,.1); }

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .adm-fade   { animation: fadeUp .5s ease both; }
    .adm-fade-1 { animation-delay: .05s; }
    .adm-fade-2 { animation-delay: .12s; }
    .adm-fade-3 { animation-delay: .2s; }
  `;
  document.head.appendChild(el);
};

/* ─── Status config ──────────────────────────────────────────── */
const STATUS_CFG = {
  pending:    { color: "#f59e0b", bg: "#fef3c7", label: "Pending",    progress: 30,  progressStatus: "normal"    },
  approved:   { color: "#3b82f6", bg: "#dbeafe", label: "Approved",   progress: 60,  progressStatus: "active"    },
  "follow-up":{ color: "#8b5cf6", bg: "#ede9fe", label: "Follow-up",  progress: 90,  progressStatus: "active"    },
  completed:  { color: "#10b981", bg: "#d1fae5", label: "Completed",  progress: 100, progressStatus: "success"   },
  rejected:   { color: "#ef4444", bg: "#fee2e2", label: "Rejected",   progress: 0,   progressStatus: "exception" },
  canceled:   { color: "#94a3b8", bg: "#f1f5f9", label: "Canceled",   progress: 0,   progressStatus: "exception" },
};
const getStatusCfg = (s) => STATUS_CFG[s?.toLowerCase()] || { color: MUTED, bg: SURFACE, label: s, progress: 0, progressStatus: "normal" };

/* ─── Custom chart tooltip ───────────────────────────────────── */
const CustomTooltip = ({ active, payload, label, prefix = "", suffix = "" }) => {
  if (active && payload?.length) {
    return (
      <div className="adm-tooltip">
        <Text strong style={{ display: "block", color: SLATE, marginBottom: 4 }}>{label}</Text>
        {payload.map((p, i) => (
          <Text key={i} style={{ color: p.color || EMERALD, display: "block" }}>
            {prefix}{p.value}{suffix} {p.name}
          </Text>
        ))}
      </div>
    );
  }
  return null;
};

/* ─── Stat card ──────────────────────────────────────────────── */
function StatCard({ title, value, icon, accentColor = EMERALD, accentBg = TEAL_LIGHT, trend }) {
  return (
    <Card className="adm-stat" bodyStyle={{ padding: "22px 24px" }}>
      <Flex align="flex-start" justify="space-between">
        <div>
          <Text style={{ color: MUTED, fontSize: 12, fontWeight: 600, display: "block", marginBottom: 6, letterSpacing: ".3px", textTransform: "uppercase" }}>
            {title}
          </Text>
          <Title level={2} style={{ margin: 0, fontFamily: "'Sora', sans-serif", color: SLATE, lineHeight: 1.1 }}>
            {value ?? "—"}
          </Title>
          {trend !== undefined && (
            <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
              {trend > 0
                ? <ArrowUpOutlined style={{ color: "#10b981", fontSize: 12 }} />
                : <ArrowDownOutlined style={{ color: "#ef4444", fontSize: 12 }} />}
              <Text style={{ fontSize: 11, color: trend > 0 ? "#10b981" : "#ef4444" }}>
                {trend > 0 ? "Active" : "Inactive"}
              </Text>
            </div>
          )}
        </div>
        <div style={{
          width: 48, height: 48, borderRadius: 14, flexShrink: 0,
          background: accentBg, display: "flex", alignItems: "center",
          justifyContent: "center", color: accentColor, fontSize: 20
        }}>
          {icon}
        </div>
      </Flex>
    </Card>
  );
}

/* ─── Chart card wrapper ─────────────────────────────────────── */
function ChartCard({ title, children }) {
  return (
    <Card className="adm-chart-card" title={title} bodyStyle={{ padding: "20px 16px 16px" }}>
      {children}
    </Card>
  );
}

/* ─── Pie label ──────────────────────────────────────────────── */
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

/* ─── Main component ─────────────────────────────────────────── */
function AdminHome() {
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState([]);
  const [chartData, setChartData] = useState({});
  const dispatch = useDispatch();

  useEffect(() => { injectStyles(); }, []);

  const processChartData = (data) => {
    const services = {}, locations = {}, doctors = {}, fees = {};
    data.forEach(a => {
      const svc = a.doctorInfo?.service || "Unknown";
      const loc = a.doctorInfo?.location || "Unknown";
      const doc = `${a.doctorInfo?.firstName || ""} ${a.doctorInfo?.lastName || ""}`.trim() || "Unknown";
      const fee = a.doctorInfo?.feePerConsultation || 0;
      services[svc] = (services[svc] || 0) + 1;
      locations[loc] = (locations[loc] || 0) + 1;
      doctors[doc]   = (doctors[doc]   || 0) + 1;
      if (!fees[svc]) fees[svc] = { total: 0, count: 0 };
      fees[svc].total += fee; fees[svc].count += 1;
    });
    const top = (obj, key = "value") => Object.entries(obj).sort(([,a],[,b])=>b-a).slice(0,5).map(([name,v])=>({ name, [key]: v }));
    return {
      topServices:  top(services),
      topLocations: top(locations, "appointments"),
      topDoctors:   top(doctors, "appointments"),
      topFees: Object.entries(fees).sort(([,a],[,b])=>b.total/b.count - a.total/a.count).slice(0,5)
        .map(([name,d])=>({ name, fee: Math.round(d.total/d.count) }))
    };
  };

  const buildStats = (raw) => [
    { title: "Total Appointments",    value: raw.totalAppointments,    icon: <CalendarOutlined />,    accentColor: EMERALD,   accentBg: TEAL_LIGHT,  trend: raw.totalAppointments },
    { title: "Pending Approvals",     value: raw.pendingApprovals,     icon: <ClockCircleOutlined />, accentColor: "#f59e0b", accentBg: "#fef3c7",   trend: -raw.pendingApprovals },
    { title: "Approved",              value: raw.approvedAppointments, icon: <CheckCircleOutlined />, accentColor: "#3b82f6", accentBg: "#dbeafe",   trend: raw.approvedAppointments },
    { title: "Completed",             value: raw.completedAppointments,icon: <CheckCircleOutlined />, accentColor: "#10b981", accentBg: "#d1fae5",   trend: raw.completedAppointments },
    { title: "Rejected",             value: raw.rejectedAppointments,icon: <StopOutlined />, accentColor: "#cd220bff", accentBg: "#d1fae5",   trend: raw.rejectedAppointments },
    { title: "Canceled",             value: raw.canceledAppointments,icon: <FileExcelOutlined />, accentColor: "#cd220bff", accentBg: "#d1fae5",   trend: raw.canceledAppointments},
    { title: "Follow-ups",            value: raw.followupAppointments, icon: <SyncOutlined />,        accentColor: "#8b5cf6", accentBg: "#ede9fe",   trend: raw.followupAppointments },
    { title: "Total Doctors",         value: raw.totalDoctors,         icon: <MedicineBoxOutlined />, accentColor: EMERALD,   accentBg: TEAL_LIGHT, trend: raw.totalDoctors  },
    { title: "Active Patients",       value: raw.activePatients,       icon: <TeamOutlined />,        accentColor: "#ab0743ff", accentBg: "#e0e7ff",trend: raw.activePatients    },
    { title: "Inactive Patients",       value: raw.inactivePatients,       icon: <ExclamationOutlined />,        accentColor: "#a60606ff", accentBg: "#e0e7ff",trend: raw.inactivePatients   },
    { title: "Blocked Patients",       value: raw.blockPatients,       icon: <CloseSquareOutlined />,        accentColor: "#ae0d0dff", accentBg: "#e0e7ff",trend: raw.blockPatients    },
    { title: "New Registrations",     value: raw.newRegistrations,     icon: <UserAddOutlined />,     accentColor: "#f59e0b", accentBg: "#fef3c7",   trend: raw.newRegistrations },
  ];

   useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(showLoading());
        const token = "Bearer " + localStorage.getItem("token");
        const [apptRes, statsRes] = await Promise.all([
          axios.get("/api/admin/get-all-appointments", { headers: { Authorization: token } }),
          axios.get("/api/admin/dashboard-stats",      { headers: { Authorization: token } }),
        ]);
        if (apptRes.data.success) {
          setAppointments(apptRes.data.data);
          setChartData(processChartData(apptRes.data.data));
        }
        if (statsRes.data.success) setStats(buildStats(statsRes.data.data));
      } catch (err) { console.error(err); }
      finally { dispatch(hideLoading()); }
    };
    fetchData();
  }, [dispatch]);

  /* Table columns */
  const columns = [
    {
      title: "Doctor", key: "doctor", width: 180,
      render: (_, r) => (
        <Flex align="center" gap={10}>
          <Avatar size={34} style={{ background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`, fontSize: 13, flexShrink: 0 }}>
            {r.doctorInfo?.firstName?.[0]}
          </Avatar>
          <div>
            <Text strong style={{ fontSize: 13, display: "block", color: SLATE }}>
              Dr. {r.doctorInfo?.firstName} {r.doctorInfo?.lastName}
            </Text>
            <Text style={{ fontSize: 11, color: MUTED }}>{r.doctorInfo?.service}</Text>
          </div>
        </Flex>
      )
    },
    {
      title: "Patient", key: "patient", width: 140,
      render: (_, r) => (
        <Flex align="center" gap={8}>
          <Avatar size={28} style={{ background: "#e0e7ff", color: "#6366f1", fontSize: 12 }}>
            {r.userInfo?.name?.[0]}
          </Avatar>
          <Text style={{ fontSize: 13 }}>{r.userInfo?.name || "N/A"}</Text>
        </Flex>
      )
    },
    {
      title: "Date", dataIndex: "date", key: "date", width: 120,
      render: (d) => <Text style={{ fontSize: 13, color: SLATE_MID }}>{d}</Text>
    },
    {
      title: "Status", dataIndex: "status", key: "status", width: 120,
      render: (status) => {
        const cfg = getStatusCfg(status);
        return (
          <Tag style={{ background: cfg.bg, color: cfg.color, border: "none", borderRadius: 20, fontWeight: 600, fontSize: 11 }}>
            {cfg.label}
          </Tag>
        );
      }
    },
    {
      title: "Progress", key: "progress", width: 160,
      render: (_, r) => {
        const cfg = getStatusCfg(r.status);
        return (
          <Progress
            percent={cfg.progress} size="small" status={cfg.progressStatus}
            strokeColor={cfg.color} showInfo={false}
            style={{ marginBottom: 0 }}
          />
        );
      }
    },
  ];

  const hasData = (d) => d && d.length > 0;

  return (
    <Layout>
      <div className="adm-root">
        <div className="adm-wrap">

          {/* ── Hero header ── */}
          <div className="adm-hero adm-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="adm-eyebrow"><DashboardOutlined style={{ marginRight: 5 }} />Admin Dashboard</div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora', sans-serif", color: WHITE, fontWeight: 800 }}>
                Platform Overview
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Monitor appointments, doctors, and patient activity in real time
              </Text>
            </div>
          </div>

          {/* ── Stats grid ── */}
          <div className="adm-fade adm-fade-1" style={{ marginBottom: 36 }}>
            <div className="adm-eyebrow" style={{ marginBottom: 16 }}>Key Metrics</div>
            <Row gutter={[20, 20]}>
              {stats.map((s, i) => (
                <Col key={i} xs={24} sm={12} md={8} lg={8}>
                  <StatCard {...s} />
                </Col>
              ))}
            </Row>
          </div>

          {/* ── Charts ── */}
          <div className="adm-fade adm-fade-2" style={{ marginBottom: 36 }}>
            <div className="adm-eyebrow" style={{ marginBottom: 16 }}>Analytics</div>
            <Row gutter={[20, 20]}>

              {/* Pie */}
              <Col xs={24} lg={12}>
                <ChartCard title="Top Booked Services">
                  {hasData(chartData.topServices) ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={chartData.topServices} cx="50%" cy="50%" labelLine={false}
                          label={renderPieLabel} outerRadius={95} dataKey="value">
                          {chartData.topServices.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip suffix="" />} />
                        <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <div className="adm-chart-empty">No data available</div>}
                </ChartCard>
              </Col>

              {/* Locations bar */}
              <Col xs={24} lg={12}>
                <ChartCard title="Top Booked Locations">
                  {hasData(chartData.topLocations) ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={chartData.topLocations} barSize={28}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="name" angle={-35} textAnchor="end" height={70} tick={{ fontSize: 11, fill: MUTED }} />
                        <YAxis tick={{ fontSize: 11, fill: MUTED }} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip suffix="" />} />
                        <Bar dataKey="appointments" name="Appointments" radius={[6,6,0,0]}>
                          {chartData.topLocations?.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div className="adm-chart-empty">No data available</div>}
                </ChartCard>
              </Col>

              {/* Doctors bar */}
              <Col xs={24} lg={12}>
                <ChartCard title="Top Booked Doctors">
                  {hasData(chartData.topDoctors) ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={chartData.topDoctors} barSize={28}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="name" angle={-35} textAnchor="end" height={70} tick={{ fontSize: 11, fill: MUTED }} />
                        <YAxis tick={{ fontSize: 11, fill: MUTED }} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip suffix="" />} />
                        <Bar dataKey="appointments" name="Appointments" radius={[6,6,0,0]}>
                          {chartData.topDoctors?.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div className="adm-chart-empty">No data available</div>}
                </ChartCard>
              </Col>

              {/* Fees bar */}
              <Col xs={24} lg={12}>
                <ChartCard title="Avg Consultation Fees by Service">
                  {hasData(chartData.topFees) ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={chartData.topFees} barSize={28}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="name" angle={-35} textAnchor="end" height={70} tick={{ fontSize: 11, fill: MUTED }} />
                        <YAxis tickFormatter={v => `₦${v}`} tick={{ fontSize: 11, fill: MUTED }} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip prefix="₦" suffix="" />} />
                        <Bar dataKey="fee" name="Avg Fee (₦)" radius={[6,6,0,0]}>
                          {chartData.topFees?.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div className="adm-chart-empty">No data available</div>}
                </ChartCard>
              </Col>

            </Row>
          </div>

          {/* ── Appointments table ── */}
          <div className="adm-fade adm-fade-3">
            <div className="adm-eyebrow" style={{ marginBottom: 16 }}>All Appointments</div>
            <Card
              className="adm-table-card"
              title="Appointment Records"
              bodyStyle={{ padding: 0 }}
              extra={
                <Tag style={{ background: TEAL_LIGHT, color: EMERALD, border: "none", borderRadius: 20, fontWeight: 700 }}>
                  {appointments.length} total
                </Tag>
              }
            >
              <Table
                dataSource={appointments}
                columns={columns}
                rowKey="_id"
                scroll={{ x: 800 }}
                pagination={{ pageSize: 10, showSizeChanger: false, style: { padding: "16px 24px" } }}
              />
            </Card>
          </div>

        </div>
      </div>
    </Layout>
  );
}

export default AdminHome;